import React from 'react';
import { 
  Building2, GraduationCap, MapPin, Users, Award, 
  ExternalLink, Mail, ShieldCheck, Laptop, BookOpen 
} from 'lucide-react';
import { FACULTY_MEMBERS, INSTITUTION_INFO } from '../data/portalData';

export const FacultyAndCampuses = () => {
  return (
    <section id="executive-faculty" className="py-20 bg-slate-50 border-b-2 border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        
        {/* Sub-Section 1: Executive University Leadership */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
              <Users className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Institutional Executive Leadership</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
              Principal Officers & Executive Leadership
            </h2>
            <p className="mt-4 text-base text-slate-600">
              Steering the academic excellence, global accreditation, and executive transformation of Akwa Ibom State University Business School.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            
            {/* 1. FIRST (LEFT): VICE CHANCELLOR */}
            <div className="translucent-card-light rounded-3xl p-6 sm:p-8 shadow-md hover:shadow-2xl transition-all flex flex-col justify-between hover:border-[#7A470E] hover:scale-[1.01] bg-white border-2 border-slate-200/90 relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#7A470E] to-[#0B2644]" />
              
              <div>
                <div className="relative mb-5 text-center pt-2">
                  <div className="relative inline-block">
                    <img 
                      src="/vice-chancellor.png" 
                      alt="Professor Otoabasi Etim Akpan"
                      className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl mx-auto object-cover border-3 border-[#7A470E] shadow-lg group-hover:scale-105 transition-transform" 
                    />
                    <span className="absolute -bottom-2 -right-2 bg-[#7A470E] text-amber-200 p-1.5 rounded-xl border-2 border-white shadow-md" title="Chief Academic Officer">
                      <Award className="w-4 h-4" />
                    </span>
                  </div>
                </div>

                <div className="text-center">
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#0B2644] bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-200">
                    Chief Academic & Executive Officer
                  </span>
                  <h3 className="font-black text-xl sm:text-2xl text-slate-900 font-sc mt-2">
                    Professor Otoabasi Etim Akpan
                  </h3>
                  <p className="text-xs sm:text-sm font-bold text-[#7A470E] mt-1 font-sc">
                    Vice Chancellor, Akwa Ibom State University
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    BA (Hons), MA, PhD, FHSN
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-600 space-y-3">
                  <div>
                    <span className="font-bold text-slate-800 font-sc block text-xs">Core Specialisation:</span>
                    <span className="text-xs text-slate-600 leading-relaxed block mt-0.5">
                      Institutional Governance, African Economic History, International Relations & Strategic Leadership
                    </span>
                  </div>

                  <div>
                    <span className="font-bold text-slate-800 font-sc block text-xs">Executive Portfolio:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {["Vice Chancellor", "University Senate Chair", "National Accreditation"].map(tag => (
                        <span key={tag} className="text-[10px] bg-slate-100 text-[#0B2644] font-mono font-bold px-2 py-0.5 rounded-lg border border-slate-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <a 
                  href="mailto:vc@aksu.edu.ng"
                  className="flex items-center hover:text-[#0B2644] transition-colors text-xs font-bold text-[#7A470E] font-sc"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5 text-[#7A470E]" />
                  <span>Office of the Vice Chancellor</span>
                </a>
                <span className="text-[10px] bg-amber-50 text-[#7A470E] font-bold px-2.5 py-1 rounded-full border border-amber-200 font-mono">
                  Principal Officer
                </span>
              </div>
            </div>

            {/* 2. SECOND (RIGHT): DIRECTOR */}
            <div className="translucent-card-light rounded-3xl p-6 sm:p-8 shadow-md hover:shadow-2xl transition-all flex flex-col justify-between hover:border-[#7A470E] hover:scale-[1.01] bg-white border-2 border-slate-200/90 relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0B2644] to-[#7A470E]" />
              
              <div>
                <div className="relative mb-5 text-center pt-2">
                  <div className="relative inline-block">
                    <img 
                      src="/director-uwah.png" 
                      alt="Dr Uwem Etim Uwah"
                      className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl mx-auto object-cover border-3 border-[#7A470E] shadow-lg group-hover:scale-105 transition-transform" 
                    />
                    <span className="absolute -bottom-2 -right-2 bg-[#0B2644] text-amber-300 p-1.5 rounded-xl border-2 border-white shadow-md" title="Directorate Authority">
                      <ShieldCheck className="w-4 h-4" />
                    </span>
                  </div>
                </div>

                <div className="text-center">
                  <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#7A470E] bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                    Directorate of the Business School
                  </span>
                  <h3 className="font-black text-xl sm:text-2xl text-slate-900 font-sc mt-2">
                    Dr Uwem Etim Uwah
                  </h3>
                  <p className="text-xs sm:text-sm font-bold text-[#7A470E] mt-1 font-sc">
                    Acting Director / Associate Professor
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    PhD, FCA, ACTI
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-600 space-y-3">
                  <div>
                    <span className="font-bold text-slate-800 font-sc block text-xs">Core Specialisation:</span>
                    <span className="text-xs text-slate-600 leading-relaxed block mt-0.5">
                      Corporate Financial Reporting, Forensic Accounting & Strategic Auditing
                    </span>
                  </div>

                  <div>
                    <span className="font-bold text-slate-800 font-sc block text-xs">Institutional Mandate:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {["Directorate Oversight", "Senate Academic Board", "Executive Curriculum"].map(tag => (
                        <span key={tag} className="text-[10px] bg-slate-100 text-[#0B2644] font-mono font-bold px-2 py-0.5 rounded-lg border border-slate-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <a 
                  href="mailto:uwemuwah@aksu.edu.ng"
                  className="flex items-center hover:text-[#0B2644] transition-colors text-xs font-bold text-[#7A470E] font-sc"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5 text-[#7A470E]" />
                  <span>Contact Director</span>
                </a>
                <span className="text-[10px] bg-emerald-50 text-emerald-800 font-bold px-2.5 py-1 rounded-full border border-emerald-200 font-mono">
                  Active in Chambers
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* Dual Institutional Branding Banner (University Seal & Business School Crest) */}
        <div className="my-14 relative overflow-hidden rounded-3xl shadow-2xl border border-amber-500/25 bg-gradient-to-r from-[#030F1C] via-[#043324] to-[#7A470E] py-10 sm:py-14 px-6 sm:px-10 text-center">
          {/* Subtle Ambient Radial Highlight */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-300/10 via-transparent to-transparent pointer-events-none" />
          
          <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
            {/* Dual White Badge Cards with Vertical Divider */}
            <div className="flex items-center justify-center space-x-4 sm:space-x-6">
              {/* Left Badge: Main University Seal */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-white rounded-2xl sm:rounded-3xl p-2.5 sm:p-3 shadow-2xl border border-white/50 flex items-center justify-center transition-transform hover:scale-105 duration-300">
                <img 
                  src="/Logo.png" 
                  alt="Akwa Ibom State University Official Crest" 
                  className="w-full h-full object-contain select-none" 
                />
              </div>

              {/* Vertical Dividing Line */}
              <div className="h-14 sm:h-18 md:h-20 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent rounded-full shadow-sm" />

              {/* Right Badge: Business School Shield */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-white rounded-2xl sm:rounded-3xl p-2.5 sm:p-3 shadow-2xl border border-white/50 flex items-center justify-center transition-transform hover:scale-105 duration-300">
                <img 
                  src="/AKSU_Business_School_Logo_Flat.svg" 
                  alt="AKSU Business School Official Shield Logo" 
                  className="w-full h-full object-contain select-none" 
                />
              </div>
            </div>

            {/* Official Title Underneath Logos */}
            <h3 className="mt-6 text-2xl sm:text-4xl md:text-5xl font-black text-white font-sc tracking-wide drop-shadow-md">
              Akwa Ibom State University Business School
            </h3>

            {/* Gold Accent Underline */}
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-amber-400 via-[#B87A28] to-amber-400 mx-auto mt-3 rounded-full shadow-sm" />

            {/* Institutional Tagline */}
            <p className="mt-3 text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-amber-200/90 font-sc">
              Knowledge · Enterprise · Leadership
            </p>
          </div>
        </div>

        {/* Sub-Section 2: Executive Campuses & State-of-the-Art Facilities */}
        <div>
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/40 mb-3 font-sc">
              <Building2 className="w-3.5 h-3.5 text-[#7A470E]" />
              <span>Campus Location · Main Campus Ikot Akpaden</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-[#0B2644] font-sc">
              Main Campus at Ikot Akpaden, Mkpat Enin
            </h2>
            <p className="mt-4 text-base text-slate-600">
              Akwa Ibom State University Business School is situated at the University Main Campus in Ikot Akpaden, 
              Mkpat Enin Local Government Area—delivering executive education in a serene, state-of-the-art environment with hybrid digital connectivity.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {(INSTITUTION_INFO?.campuses || []).map((camp, idx) => (
              <div 
                key={idx}
                className="translucent-card-light rounded-3xl p-8 sm:p-10 shadow-lg hover:shadow-2xl transition-all flex flex-col justify-between group hover:border-[#7A470E]"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 text-xs font-black rounded-lg bg-[#0B2644] text-white font-sc">
                      {camp.type}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center">
                      <MapPin className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                      {camp.location}
                    </span>
                  </div>

                  <h3 className="text-2xl font-black text-slate-900 group-hover:text-[#0B2644] transition-colors font-sc">
                    {camp.name}
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed">
                    {camp.description}
                  </p>

                  <div className="mt-6 pt-4 border-t border-slate-100 space-y-2">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider font-sc block text-[#7A470E]">
                      Key Facilities:
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                      {camp.features.map((feat, fIdx) => (
                        <div key={fIdx} className="flex items-center space-x-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#7A470E]" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-mono">
                  <span>State-of-the-Art Executive Suites</span>
                  <span className="font-bold text-[#0B2644] font-sc">Hybrid Stream Active</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
